import { useState } from 'react'
import PropTypes from 'prop-types'

import { Box, Button, Tooltip, Typography } from '@material-ui/core'
import { TableRow, TableCell } from 'components/Table'
import { ConfirmationDialog, LoadingFeedback, MenuButton } from 'components'

import * as service from 'service'
import useSnackbar from 'hooks/useSnackbar'

import ImportInfo from '../ImportInfo'

const ENABLE_DAI_TAG = 'enable_dai_dispute_analysis'
const DAI_ONLY_SEGCOOP_TOOLTIP =
  'A DAI de Contestação está disponível apenas para projetos importados do Segcoop'

const DIALOG_INITIAL = { open: false, message: '', action: null }

const TableRowGovernanceProject = ({ governanceProject, refresh }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [dialog, setDialog] = useState(DIALOG_INITIAL)
  const snackbar = useSnackbar()

  const daiSetting = governanceProject?.governanceProjectSettings?.find(
    (governanceProjectSetting) =>
      governanceProjectSetting.tag === ENABLE_DAI_TAG,
  )
  const isEnabled = daiSetting?.value === true
  const hasImport = !!governanceProject?.governanceProjectImport

  const closeDialog = () => setDialog(DIALOG_INITIAL)

  const confirmToggle = () =>
    setDialog({
      open: true,
      message: isEnabled
        ? 'Deseja desabilitar a DAI de Contestação para este projeto?'
        : 'Deseja habilitar a DAI de Contestação para este projeto?',
      action: executeToggle,
    })

  const confirmDaiDisputeAnalysis = () =>
    setDialog({
      open: true,
      message: 'Deseja executar a DAI de Contestação para este projeto?',
      action: executeDaiDisputeAnalysis,
    })

  const executeToggle = async () => {
    closeDialog()
    setIsLoading(true)

    try {
      if (!daiSetting) {
        await service.backoffice.governanceProjects.createSetting({
          governanceProjectId: governanceProject.id,
          name: 'Habilitar análise de contestação via DAI?',
          tag: ENABLE_DAI_TAG,
          value: true,
        })
      } else {
        await service.backoffice.governanceProjects.updateSetting({
          governanceProjectId: governanceProject.id,
          settingId: daiSetting.id,
          value: !isEnabled,
        })
      }
      snackbar.open({
        message: isEnabled
          ? 'DAI de Contestação desabilitada com sucesso!'
          : 'DAI de Contestação habilitada com sucesso!',
        variant: 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao atualizar a configuração.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const executeDaiDisputeAnalysis = async () => {
    closeDialog()
    setIsLoading(true)
    try {
      await service.backoffice.governanceProjects.daiDisputeAnalysis({
        governanceProjectId: governanceProject.id,
      })
      snackbar.open({
        message: 'DAI de Contestação executada com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao executar a DAI de Contestação.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={isLoading} />
      <ConfirmationDialog
        open={dialog.open}
        setOpen={closeDialog}
        message={dialog.message}
        buttonText="confirmar"
        actionAcceptButton={dialog.action}
      />
      <TableRow hover>
        <TableCell disableTooltip>{governanceProject?.id}</TableCell>
        <TableCell disableTooltip>{governanceProject?.name}</TableCell>
        <TableCell disableTooltip>
          <Box>
            <Typography variant="body2">
              {governanceProject?.company?.name || '-'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {governanceProject?.company?.document || '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell disableTooltip>
          {governanceProject?.governanceProjectTemplate?.currentStage?.name ||
            '-'}
        </TableCell>
        <TableCell disableTooltip>
          <ImportInfo importData={governanceProject?.governanceProjectImport} />
        </TableCell>
        <TableCell disableTooltip align="right">
          <MenuButton>
            <Tooltip title={!hasImport ? DAI_ONLY_SEGCOOP_TOOLTIP : ''}>
              <span>
                <Button
                  fullWidth
                  size="small"
                  onClick={confirmToggle}
                  disabled={!hasImport}
                >
                  {isEnabled
                    ? 'Desabilitar DAI de Contestação'
                    : 'Habilitar DAI de Contestação'}
                </Button>
              </span>
            </Tooltip>
            {isEnabled && (
              <Tooltip title={!hasImport ? DAI_ONLY_SEGCOOP_TOOLTIP : ''}>
                <span>
                  <Button
                    fullWidth
                    size="small"
                    onClick={confirmDaiDisputeAnalysis}
                    disabled={!hasImport}
                  >
                    Executar DAI de Contestação
                  </Button>
                </span>
              </Tooltip>
            )}
          </MenuButton>
        </TableCell>
      </TableRow>
    </>
  )
}

TableRowGovernanceProject.propTypes = {
  governanceProject: PropTypes.object,
  refresh: PropTypes.func,
}

TableRowGovernanceProject.defaultProps = {
  refresh: () => {},
}

export default TableRowGovernanceProject
